import { HeaderData } from 'whocan-lib';
import { genericColorPaletteMap } from '../../core/poll-templates/poll-config/generic-themes/generic-color-pallete.config';
import { genericStyleMap } from '../../core/poll-templates/poll-config/generic-themes/generic-style.config';
import { genericThemeMap } from '../../core/poll-templates/poll-config/generic-themes/generic-theme.config';
import { invitationColorPaletteMap } from '../../core/poll-templates/poll-config/invitation-themes/inivitation-color-pallete.config';
import { invitationStyleMap } from '../../core/poll-templates/poll-config/invitation-themes/invitation-style.config';
import { invitationThemeMap } from '../../core/poll-templates/poll-config/invitation-themes/invitation-theme.config';

/**
 * Layout switch (concept-layout-switch-invitation-2026-10-01 R1-R3): the theme family follows the layout
 * (`formToUse`). When the owner switches between the families the design fields that are bound to a family
 * are migrated: what exists in the target family stays, the rest falls back to the family default.
 * Pure functions without an Angular dependency.
 */
export type ThemeFamily = 'invitation' | 'generic'
export type ThemeFamilyMigrationField = 'theme' | 'pollStyleKey' | 'colorPalette'
export type ThemeFamilyMigrationResult = { resetFields: ThemeFamilyMigrationField[] }

export const familyDefaultTheme: Record<ThemeFamily, string> = { invitation: 'ballons', generic: 'no-theme' }

export function themeFamilyOf(formToUse: string | undefined): ThemeFamily {
    return formToUse === 'invitation' ? 'invitation' : 'generic'
}

/**
 * Migrates the family-bound design fields of a header in place and returns which ones were reset.
 * - theme: stays when the key exists in the target family - plain key overlap, `no-theme` included
 *   (workorder-layout-switch-invitation-2026-10-01, addendum 2: an advanced poll switched to the invitation
 *   layout starts without a background, the owner picks the theme). Otherwise the template default
 *   (`defaultSettings.theme`) when it belongs to the target family, else the family default, which therefore
 *   only applies when both are missing in the target family. `custom` exists in both families, so an uploaded
 *   background stays.
 * - pollStyleKey / colorPalette: removed when the key does not exist in the target family - the value then
 *   comes from the theme config. Removed means `null`, not absent: HeaderService.persist() writes the header
 *   with a firebase update, which leaves absent keys untouched but deletes keys set to null.
 * - everything else (backgroundSizeKey*, fontColor*, backgroundColor, fontFamily, fontSize*, backgroundUrl,
 *   registerPeople, askForPublicComments ...) is left alone: neutral, or ignored by the target family and
 *   back in place when the owner switches back.
 */
export function migrateThemeFieldsForLayout(
    header: HeaderData,
    target: ThemeFamily,
    templateDefaultTheme: string | undefined
): ThemeFamilyMigrationResult {
    const resetFields: ThemeFamilyMigrationField[] = []
    const themeMap: Map<string, unknown> = target === 'invitation' ? invitationThemeMap : genericThemeMap
    const styleMap: Map<string, unknown> = target === 'invitation' ? invitationStyleMap : genericStyleMap
    const paletteMap: Map<string, unknown> = target === 'invitation' ? invitationColorPaletteMap : genericColorPaletteMap
    const isValidTheme = (themeKey: string | undefined) => !!themeKey && themeMap.has(themeKey)

    // a header without a theme renders the template default - nothing to reset when that one fits the target family
    if (!isValidTheme(header.theme || templateDefaultTheme)) {
        header.theme = isValidTheme(templateDefaultTheme) ? templateDefaultTheme : familyDefaultTheme[target]
        resetFields.push('theme')
    }
    if (header.pollStyleKey && !styleMap.has(header.pollStyleKey)) {
        header.pollStyleKey = null
        resetFields.push('pollStyleKey')
    }
    if (header.colorPalette && !paletteMap.has(header.colorPalette)) {
        header.colorPalette = null
        resetFields.push('colorPalette')
    }
    return { resetFields }
}
